import type { CaptureResult } from 'posthog-js'
import { redactProviderKeys } from '@/shared/lib/redactProviderKeys'
import { sanitizeSensitiveUrl } from '@/shared/lib/sanitizeSensitiveUrl'

const URL_PROPERTY_KEYS = new Set([
  '$current_url',
  '$pathname',
  '$referrer',
  '$host',
  '$initial_referrer',
  '$initial_current_url',
  '$prev_pageview_pathname',
  '$prev_pageview_url',
])

const looksLikeSensitiveUrl = (value: string): boolean =>
  /^https?:\/\//i.test(value) ||
  value.startsWith('/') ||
  value.includes('/patients/') ||
  value.includes('/r/') ||
  value.includes('/exam-share/')

const sanitizeUrlProperty = (value: string): string => {
  if (!looksLikeSensitiveUrl(value)) {
    return value
  }
  return sanitizeSensitiveUrl(value)
}

export const sanitizePostHogEventProperties = (
  properties: Record<string, unknown>,
): Record<string, unknown> => {
  const sanitized: Record<string, unknown> = { ...properties }

  for (const [key, rawValue] of Object.entries(sanitized)) {
    if (typeof rawValue !== 'string') {
      continue
    }

    const value = redactProviderKeys(rawValue)
    sanitized[key] = value

    if (URL_PROPERTY_KEYS.has(key) || key.endsWith('_url') || key.endsWith('_pathname')) {
      sanitized[key] = sanitizeUrlProperty(value)
      continue
    }

    if (looksLikeSensitiveUrl(value)) {
      sanitized[key] = sanitizeUrlProperty(value)
    }
  }

  return sanitized
}

/** Global before_send — parity with Sentry beforeSend (CRIT-001 / SEC-008). */
export const sanitizePostHogBeforeSend = (event: CaptureResult | null): CaptureResult | null => {
  if (!event) {
    return null
  }

  const properties = event.properties
  if (!properties || typeof properties !== 'object') {
    return event
  }

  return {
    ...event,
    properties: sanitizePostHogEventProperties(properties as Record<string, unknown>) as CaptureResult['properties'],
  }
}
